/**
 * The proposal form.
 *
 * This is the only thing on the site a visitor can do, so it gets the whole
 * final section rather than a modal. A modal is right for a one-field waitlist
 * — which is what the product sites use it for — and wrong for seven fields
 * someone needs to think about, where losing the page behind an overlay costs
 * them the context they are describing.
 *
 * ── Passy carries the state ───────────────────────────────────────────────
 * `building` while the request is in flight, `shipped` once it lands. That is
 * exactly what the kit says those faces are for: "deploys, form submits,
 * anything that takes over a second", and the wink "only after something real
 * shipped". A failed submit deliberately does NOT show the `failing` face —
 * see the note in Passy.tsx.
 *
 * ── Validation ────────────────────────────────────────────────────────────
 * One implementation, in shared/proposal.ts, run here before the request goes
 * out and again on the server when it arrives. The browser's copy is only a
 * courtesy; the server's is the one that counts.
 */
import { useId, useState, type FormEvent } from 'react';

import { Passy } from '../components/Passy';
import { Section } from '../components/Section';
import { useI18n } from '../i18n';
import { COMPANY, LAUNCH_OFFER_OPEN } from '../lib/constants';
import { sendProposal } from '../lib/proposalApi';
import {
  validateProposal,
  type ProposalErrorCode,
  type ProposalField,
} from '../../shared/proposal';
import styles from './Proposal.module.css';

type Status = 'idle' | 'sending' | 'sent';

export function Proposal() {
  const { t, locale } = useI18n();
  const formId = useId();

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<ProposalErrorCode | null>(null);
  const [badField, setBadField] = useState<ProposalField | null>(null);

  const field = (name: string) => `${formId}-${name}`;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'sending') return;

    const data = new FormData(event.currentTarget);
    const request = {
      name: String(data.get('name') ?? ''),
      company: String(data.get('company') ?? ''),
      email: String(data.get('email') ?? ''),
      kind: String(data.get('kind') ?? ''),
      budget: String(data.get('budget') ?? ''),
      deadline: String(data.get('deadline') ?? ''),
      detail: String(data.get('detail') ?? ''),
      website: String(data.get('website') ?? ''),
      locale,
    };

    // The same check the server will run. Failing here saves a round trip and
    // lets the message appear instantly.
    const check = validateProposal(request);
    if (!check.valid || !check.value) {
      setError(check.error ?? 'server_error');
      setBadField(check.field ?? null);
      return;
    }

    setStatus('sending');
    setError(null);
    setBadField(null);

    /*
     * The validated value, not the raw form data. It is already trimmed and
     * normalised, and it carries the narrowed union types — sending `request`
     * would put unvalidated strings back on the wire that the server would only
     * have to reject again.
     */
    const response = await sendProposal(check.value);

    if (response.ok) {
      setStatus('sent');
      return;
    }

    setStatus('idle');
    setError(response.error);
    setBadField(response.field ?? null);
  }

  const message = error ? (t.proposal.errors[error] ?? t.proposal.errors.server_error) : null;

  return (
    <Section id="proposal" tone="paper">
      <div className={styles.layout}>
        <div className={styles.intro}>
          <p className="overline">{t.proposal.eyebrow}</p>
          <h2 className={styles.title}>{t.proposal.title}</h2>
          <p className={styles.body}>{t.proposal.body}</p>

          <div className={styles.direct}>
            <a className={styles.email} href={`mailto:${COMPANY.email}`}>
              {COMPANY.email}
            </a>
            <p className="meta">{t.proposal.location}</p>
          </div>

          {LAUNCH_OFFER_OPEN ? <p className={styles.offerNote}>{t.proposal.offerNote}</p> : null}
        </div>

        <div className={styles.panel}>
          {status === 'sent' ? (
            /*
             * The form is replaced rather than hidden, so a screen reader moves
             * into the confirmation instead of into a form that no longer does
             * anything. `role="status"` announces it without stealing focus.
             */
            <div className={styles.done} role="status">
              <Passy state="shipped" size={56} label={t.proposal.successTitle} />
              <h3 className={styles.doneTitle}>{t.proposal.successTitle}</h3>
              <p className={styles.doneBody}>{t.proposal.successBody}</p>
            </div>
          ) : (
            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <div className={styles.row}>
                <Field
                  id={field('name')}
                  name="name"
                  label={t.proposal.fields.name}
                  autoComplete="name"
                  invalid={badField === 'name'}
                  required
                />
                <Field
                  id={field('company')}
                  name="company"
                  label={t.proposal.fields.company}
                  hint={t.proposal.fields.optional}
                  autoComplete="organization"
                />
              </div>

              <Field
                id={field('email')}
                name="email"
                type="email"
                label={t.proposal.fields.email}
                autoComplete="email"
                invalid={badField === 'email'}
                required
              />

              <div className={styles.row}>
                <SelectField
                  id={field('kind')}
                  name="kind"
                  label={t.proposal.fields.kind}
                  options={t.proposal.kinds}
                  invalid={badField === 'kind'}
                />
                <SelectField
                  id={field('budget')}
                  name="budget"
                  label={t.proposal.fields.budget}
                  options={t.proposal.budgets}
                  invalid={badField === 'budget'}
                />
              </div>

              <Field
                id={field('deadline')}
                name="deadline"
                label={t.proposal.fields.deadline}
                hint={t.proposal.fields.optional}
              />

              <div className={styles.group}>
                <label className={styles.label} htmlFor={field('detail')}>
                  {t.proposal.fields.detail}
                </label>
                <textarea
                  id={field('detail')}
                  name="detail"
                  rows={5}
                  className={`${styles.input} ${styles.textarea} ${badField === 'detail' ? styles.invalid : ''}`}
                  aria-describedby={field('detail-hint')}
                  required
                />
                <p id={field('detail-hint')} className="meta">
                  {t.proposal.fields.detailHint}
                </p>
              </div>

              {/*
                The honeypot. Hidden from people, left empty by them, and filled
                in by most naive bots. `tabIndex={-1}` keeps it off the keyboard
                path and aria-hidden keeps it out of the accessibility tree, so
                nobody using assistive technology is trapped by it.

                Named `website`, not `company` — company is a real field on this
                form, and using it as the trap would discard every request from
                somebody who has one.
              */}
              <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor={field('website')}>Website</label>
                <input
                  id={field('website')}
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {message ? (
                <p className={styles.error} role="alert">
                  {message}
                </p>
              ) : null}

              <div className={styles.actions}>
                <button type="submit" className={styles.submit} disabled={status === 'sending'}>
                  <Passy state={status === 'sending' ? 'building' : 'passing'} size={18} />
                  {status === 'sending' ? t.proposal.sending : t.proposal.submit}
                </button>
                <p className={styles.reply}>{t.proposal.reply}</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
}

interface FieldProps {
  id: string;
  name: string;
  label: string;
  hint?: string;
  type?: string;
  autoComplete?: string;
  invalid?: boolean;
  required?: boolean;
}

function Field({
  id,
  name,
  label,
  hint,
  type = 'text',
  autoComplete,
  invalid,
  required,
}: FieldProps) {
  return (
    <div className={styles.group}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {hint ? <span className={styles.hint}> · {hint}</span> : null}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        aria-invalid={invalid || undefined}
        className={`${styles.input} ${invalid ? styles.invalid : ''}`}
      />
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  name: string;
  label: string;
  options: readonly { value: string; label: string }[];
  invalid?: boolean;
}

function SelectField({ id, name, label, options, invalid }: SelectFieldProps) {
  return (
    <div className={styles.group}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        name={name}
        required
        defaultValue={options[0]?.value}
        aria-invalid={invalid || undefined}
        className={`${styles.input} ${styles.select} ${invalid ? styles.invalid : ''}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
